import { Fragment, useEffect, useState } from "react";
import type { BackupStatus } from "@quill/shared";
import { ApiError, api } from "../api";
import { useOnline } from "../hooks";
import {
  formatBarDevice,
  setFormatBar,
  useFormatBarSettings,
  type FormatBarDevice,
  type FormatBarOption,
} from "../formatBar";
import { setLaunchSetting, useLaunchSetting, type LaunchSetting } from "../launch";
import { setTheme, useTheme, type ThemeSetting } from "../theme";
import {
  setFont,
  setTextSize,
  useFont,
  useTextSize,
  type FontSetting,
  type TextSizeSetting,
} from "../typography";
import { removeFromDictionary, usePersonalDictionary } from "../spell/checker";
import { Dialog } from "./Dialog";
import { GoalFields } from "./Progress";

const THEMES: { value: ThemeSetting; label: string }[] = [
  { value: "system", label: "Auto" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const FONTS: { value: FontSetting; label: string }[] = [
  { value: "serif", label: "Serif" },
  { value: "sans", label: "Sans" },
  { value: "mono", label: "Mono" },
];

const SIZES: { value: TextSizeSetting; label: string }[] = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "larger", label: "Larger" },
];

const LAUNCH: { value: LaunchSetting; label: string }[] = [
  { value: "last", label: "Last document" },
  { value: "library", label: "Library" },
];

const FORMAT_BAR_DEVICES: { value: FormatBarDevice; label: string }[] = [
  { value: "mobile", label: "Mobile" },
  { value: "desktop", label: "Desktop" },
];

const FORMAT_BAR_OPTIONS: { value: FormatBarOption; label: string }[] = [
  { value: "always", label: "Always show" },
  { value: "side", label: "Vertical, at the side" },
];

function ago(ms: number): string {
  const min = Math.round((Date.now() - ms) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The settings sheet: theme, writing font and size, what opens on launch, the formatting bar, word goals, dictionary, "Back up now", and sign out. */
export function Settings({ onClose, onSignOut }: { onClose(): void; onSignOut(): void }) {
  const theme = useTheme();
  const font = useFont();
  const textSize = useTextSize();
  const launch = useLaunchSetting();
  const formatBar = useFormatBarSettings();
  const dictionary = usePersonalDictionary();
  const online = useOnline();
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (online) api.backupStatus().then(setStatus, () => undefined);
  }, [online]);

  async function backUp() {
    setBusy(true);
    setFailed(false);
    try {
      setStatus((await api.backupNow()).status);
    } catch (err) {
      setFailed(true);
      if (err instanceof ApiError) api.backupStatus().then(setStatus, () => undefined);
    } finally {
      setBusy(false);
    }
  }

  const pushFailed = status?.pushed === false;

  return (
    <Dialog title="Settings" onClose={onClose}>
      <fieldset className="segmented">
        <legend>Theme</legend>
        {THEMES.map((t) => (
          <label key={t.value}>
            <input
              type="radio"
              name="theme"
              value={t.value}
              checked={theme === t.value}
              onChange={() => setTheme(t.value)}
            />
            <span>{t.label}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="segmented">
        <legend>Font</legend>
        {FONTS.map((f) => (
          <label key={f.value}>
            <input type="radio" name="font" value={f.value} checked={font === f.value} onChange={() => setFont(f.value)} />
            <span className={`font-${f.value}`}>{f.label}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="segmented">
        <legend>Text size</legend>
        {SIZES.map((s) => (
          <label key={s.value}>
            <input
              type="radio"
              name="textSize"
              value={s.value}
              checked={textSize === s.value}
              onChange={() => setTextSize(s.value)}
            />
            <span>{s.label}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="segmented">
        <legend>Open on launch</legend>
        {LAUNCH.map((l) => (
          <label key={l.value}>
            <input
              type="radio"
              name="launch"
              value={l.value}
              checked={launch === l.value}
              onChange={() => setLaunchSetting(l.value)}
            />
            <span>{l.label}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="settings-format-bar">
        <legend>Formatting bar</legend>
        <span />
        {FORMAT_BAR_DEVICES.map((d) => (
          <span key={d.value} className={`head${d.value === formatBarDevice ? " current" : ""}`}>
            {d.label}
          </span>
        ))}
        {FORMAT_BAR_OPTIONS.map((o) => (
          <Fragment key={o.value}>
            <span>{o.label}</span>
            {FORMAT_BAR_DEVICES.map((d) => (
              <label key={d.value}>
                <input
                  type="checkbox"
                  aria-label={`${o.label} on ${d.label.toLowerCase()}`}
                  checked={formatBar[d.value][o.value]}
                  onChange={(e) => setFormatBar(d.value, o.value, e.target.checked)}
                />
              </label>
            ))}
          </Fragment>
        ))}
      </fieldset>

      <fieldset className="settings-goals">
        <legend>Word goals</legend>
        <GoalFields />
      </fieldset>

      <details className="settings-dictionary">
        <summary>
          Dictionary <span className="hint">({dictionary.length} {dictionary.length === 1 ? "word" : "words"})</span>
        </summary>
        {dictionary.length ? (
          <ul>
            {dictionary.map((w) => (
              <li key={w}>
                <span>{w}</span>
                <button className="quiet" aria-label={`Remove ${w}`} onClick={() => removeFromDictionary(w)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="hint">Words you add from the spelling menu appear here.</p>
        )}
      </details>

      <section className="settings-backup" aria-label="Backup">
        <button className="secondary" onClick={backUp} disabled={!online || busy}>
          {busy ? "Backing up…" : "Back up now"}
        </button>
        <p className="hint" role="status">
          {!online ? "Offline." : status ? (status.lastSuccessAt ? `Last backup ${ago(status.lastSuccessAt)}.` : "No backups yet.") : ""}
        </p>
        {(pushFailed || failed) && (
          <p className="warning">
            {pushFailed
              ? "Backups are saved on the server but couldn't be copied off it. Your writing is safe; check the backup remote."
              : "The backup didn't finish. Your writing is safe; try again later."}
          </p>
        )}
      </section>

      <div className="sheet-actions spread">
        <button className="quiet" onClick={onSignOut}>
          Sign out
        </button>
        <button className="primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
